import { createEffect, createMemo, onCleanup, Show } from 'solid-js'

import { colorTheme } from '../../state/colorThemeStore'
import { translate } from '../../i18n'
import {
   formatWeight,
   getFilteredWeightEntries,
   getHeightMeters,
   getWeightRangeTimestamps,
   setWeightChartMode,
   showWeightTarget,
   type WeightChartMode,
   type WeightEntry,
   type WeightFilterKey,
   weightChartMode,
   weightEntries,
   weightFilter,
   weightProfile,
} from '../../state/weightStore'
import {
   computeWeightTrendAnalysis,
   type DrawWeightChartOptions,
   drawWeightChart,
   type WeightTrendAnalysis,
} from './weightChartUtils'
import {
   createWeightChartHover,
   WeightChartPointTooltip,
} from './WeightChartPointTooltip'

export type WeightChartViewProps = {
   average: number | null
   chartMode: WeightChartMode
   dataPoints: WeightEntry[]
   filterKey: WeightFilterKey
   formatMassLabel?: (weightKg: number) => string
   heightMeters: number | null
   onToggleMode: () => void
   rangeEnd: number
   rangeStart: number
   showTarget: boolean
   targetWeightKg: number | null
   trendAnalysis: WeightTrendAnalysis | null
}

/**
 * Builds the draw options for the weight chart from component props.
 *
 * @param props - The chart view props.
 * @returns The draw options.
 */
function getDrawOptions(props: WeightChartViewProps): DrawWeightChartOptions {
   return {
      dataPoints: props.dataPoints,
      filterKey: props.filterKey,
      average: props.average,
      heightMeters: props.heightMeters,
      targetWeightKg: props.targetWeightKg,
      showTarget: props.showTarget,
      chartMode: props.chartMode,
      trendAnalysis: props.trendAnalysis,
      formatMassLabel: props.formatMassLabel,
   }
}

/**
 * Canvas-backed weight chart view that redraws on data and resize changes.
 *
 * @param props - The chart view props.
 * @returns The chart element.
 */
export const WeightChartView = (props: WeightChartViewProps) => {
   let canvasRef: HTMLCanvasElement | undefined
   const hover = createWeightChartHover(props)
   const formatMass = (weightKg: number) =>
      props.formatMassLabel?.(weightKg) ?? formatWeight(weightKg)

   /**
    * Redraws the chart onto the canvas.
    *
    * @param options - The draw options (defaults to the current props).
    */
   const redraw = (options = getDrawOptions(props)) => {
      const canvas = canvasRef
      const ctx = canvas?.getContext('2d')
      if (!canvas || !ctx) {
         return
      }

      hover.setPlottedPoints(drawWeightChart(canvas, ctx, options))
   }

   createEffect(() => {
      const options = getDrawOptions(props)
      // Canvas colors come from theme CSS variables, so repaint on theme switch.
      colorTheme()
      queueMicrotask(() => redraw(options))
   })

   /**
    * Redraws the chart on container resize.
    */
   const handleResize = () => redraw()
   if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize)
      onCleanup(() => window.removeEventListener('resize', handleResize))
   }

   return (
      <>
         <div
            id="weight-chart-shell"
            class="relative w-full overflow-hidden rounded-xl border border-edge-soft bg-surface/60"
         >
            <p
               id="weight-chart-hover-date"
               class="pointer-events-none absolute right-3 top-3 z-10 rounded-md bg-surface-sunken/70 px-2 py-1 text-xs font-medium text-ink-soft backdrop-blur-sm transition-opacity sm:whitespace-nowrap"
               classList={{
                  'opacity-0': !hover.hoverDate(),
                  'opacity-100': Boolean(hover.hoverDate()),
               }}
               aria-live="polite"
            >
               {hover.hoverDate()}
            </p>
            <div class="relative w-full" style="padding-bottom: 75%;">
               <canvas
                  ref={canvasRef}
                  id="weight-chart"
                  width="800"
                  height="600"
                  class="absolute inset-0 h-full w-full cursor-pointer"
                  tabIndex={0}
                  data-chart-mode={props.chartMode}
                  aria-label={translate('Weight history chart - {mode}. {instruction}', {
                     mode:
                        props.chartMode === 'scatter' ?
                           translate('Scatter with trend line')
                        :  translate('Line chart'),
                     instruction: translate('Click to toggle weight chart view.'),
                  })}
                  title={
                     hover.hoveredPoint() ? undefined : (
                        translate('Click to toggle weight chart view.')
                     )
                  }
                  onMouseMove={event =>
                     canvasRef && hover.handlePointerMove(canvasRef, event)
                  }
                  onMouseLeave={hover.clearHover}
                  onClick={props.onToggleMode}
                  onKeyDown={event => {
                     if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        props.onToggleMode()
                     }
                  }}
               />
               <Show when={hover.hoveredPoint()}>
                  {hovered => (
                     <WeightChartPointTooltip
                        hovered={hovered()}
                        formatMass={formatMass}
                     />
                  )}
               </Show>
            </div>
         </div>
      </>
   )
}

/**
 * Renders the weight chart panel with its filters and controls.
 *
 * @returns The chart panel element.
 */
const WeightChart = () => {
   const filteredEntries = createMemo(() =>
      getFilteredWeightEntries(weightEntries(), weightFilter()),
   )
   const chronologicalEntries = createMemo(() =>
      [...filteredEntries()].sort((a, b) => a.timestamp - b.timestamp),
   )
   const averageWeight = createMemo(() => {
      const entries = chronologicalEntries()
      if (!entries.length) {
         return null
      }
      return entries.reduce((sum, entry) => sum + entry.weight, 0) / entries.length
   })
   const heightMeters = createMemo(() => getHeightMeters(weightProfile()))
   const targetWeightKg = createMemo(() => weightProfile().targetWeightKg)
   const trendAnalysis = createMemo(() =>
      computeWeightTrendAnalysis(chronologicalEntries(), targetWeightKg()),
   )
   const timestampRange = createMemo(() =>
      getWeightRangeTimestamps(weightFilter(), weightEntries()),
   )

   return (
      <WeightChartView
         average={averageWeight()}
         chartMode={weightChartMode()}
         dataPoints={chronologicalEntries()}
         filterKey={weightFilter()}
         formatMassLabel={formatWeight}
         heightMeters={heightMeters()}
         onToggleMode={() =>
            setWeightChartMode(weightChartMode() === 'line' ? 'scatter' : 'line')
         }
         rangeEnd={timestampRange().rangeEnd}
         rangeStart={timestampRange().rangeStart}
         showTarget={showWeightTarget()}
         targetWeightKg={targetWeightKg()}
         trendAnalysis={trendAnalysis()}
      />
   )
}

export default WeightChart
